import { useState, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectClinicId,
  selectUser,
  selectIsAdmin,
  selectTherapistId,
  selectTherapistName,
  setClinicProfile,
} from "../../store/slices/authSlice";
import { useToast } from "../../contexts/ToastContext";
import {
  useUpdateBookingSettingsMutation,
  useUpdateSmsSettingsMutation,
  useUpdateEndMeetingSmsMutation,
  useUpdateShowAddressInApprovalLinkMutation,
  useGenerateBookingSlugMutation,
  useGetWeeklyAvailabilityQuery,
  useSaveWeeklyAvailabilityMutation,
  useGetAvailabilityTemplateQuery,
  useSaveAvailabilityTemplateMutation,
  useGetSmsSettingsQuery,
} from "../../store/services/calendarApi";
import {
  useGetCategoriesQuery,
  useUpdateCategoryMutation,
} from "../../store/services/tasksApi";
import {
  normalizeTreatmentType,
  getPublicTypeWarnings,
} from "../../utils/treatmentTypeRules";
import TreatmentTypeFormModal from "./TreatmentTypeFormModal";
import {
  getWeekId,
  getWeekStart,
  getWeekEnd,
  formatLocalDateString,
  HEBREW_DAYS_WITH_APOSTROPHE,
} from "../../utils/dateUtils";
import { addWeeks } from "date-fns";
import { trackView, VIEW_KEYS } from "../../services/telemetry";
import {
  CalendarBlank,
  CaretLeft,
  CaretRight,
  Link as LinkIcon,
  ChatText,
  Copy,
  Check,
  Plus,
  Prohibit,
  Trash,
  FloppyDisk,
  DownloadSimple,
  Info,
  UsersThree,
  Pencil,
  Globe,
  WarningCircle,
  Stethoscope,
  CalendarX,
} from "@phosphor-icons/react";
import { HelpButton } from "../shared";
import GoogleCalendarSync from "./GoogleCalendarSync";
import AbsencesPanel from "./absences/AbsencesPanel";
import TimePicker from "../ui/TimePicker";
import ModalCloseButton from "../ui/ModalCloseButton";
import gLogo from "../../assets/g-logo.png";
import ContentLoader from "../shared/ContentLoader";
import LoaderLoop from "../shared/loader/LoaderLoop";
import "./CalendarSettings.css";

// How many weeks ahead of the current week a therapist can navigate
// (current week + 52 more = a full year of planning).
const MAX_WEEK_OFFSET = 52;

const DAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

// The four settings domains shown in the rail. `key` drives the active panel;
// `dirtyKey` (when present) wires the rail's unsaved dot + the save-all flow.
const DOMAINS = [
  {
    key: "availability",
    label: "זמינות שבועית",
    short: "זמינות",
    color: "green",
    dirtyKey: "availability",
  },
  {
    key: "absences",
    label: "היעדרויות",
    short: "היעדרויות",
    color: "absences",
    dirtyKey: null,
  },
  {
    key: "booking",
    label: "קישור הזמנה",
    short: "הזמנה",
    color: "blue",
    dirtyKey: "booking",
  },
  {
    key: "sms",
    label: "הודעות SMS",
    short: "SMS",
    color: "purple",
    dirtyKey: "sms",
  },
  {
    key: "google",
    label: "Google Calendar",
    short: "Google",
    color: "yellow",
    dirtyKey: null,
  },
];

// Domains whose "was it ever opened" is a nudge signal. `availability` is the
// default panel, so opening it says nothing and it has no key.
const DOMAIN_VIEW_KEYS = {
  booking: VIEW_KEYS.CALENDAR_SETTINGS_BOOKING,
  sms: VIEW_KEYS.CALENDAR_SETTINGS_SMS,
  google: VIEW_KEYS.CALENDAR_SETTINGS_GOOGLE,
};

// targetTherapistId/targetTherapistName + availabilityOnly power the admin
// flow: a clinic admin edits another therapist's availability (windows +
// blocked times) from the shared calendar, with all other domains hidden.
function CalendarSettings({
  clinicProfile,
  onClose,
  targetTherapistId,
  targetTherapistName,
  availabilityOnly = false,
}) {
  const clinicId = useSelector(selectClinicId);
  const user = useSelector(selectUser);
  const isAdmin = useSelector(selectIsAdmin);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const dispatch = useDispatch();
  const { showSuccess, showError } = useToast();

  // Treatment types double as the public "services" list on the booking
  // page (ONE list, also managed in Clinic Settings → התאמה אישית).
  const { data: treatmentTypesRaw = [], isLoading: isLoadingTreatmentTypes } =
    useGetCategoriesQuery(
      { clinicId, type: "treatmentType" },
      { skip: !clinicId || availabilityOnly },
    );
  const treatmentTypes = useMemo(
    () => treatmentTypesRaw.map(normalizeTreatmentType),
    [treatmentTypesRaw],
  );
  const [updateCategory] = useUpdateCategoryMutation();
  const [treatmentTypeModal, setTreatmentTypeModal] = useState(null); // null | { editing }
  const [togglingTypeId, setTogglingTypeId] = useState(null);

  // Inline switches write straight to the category doc (not part of the
  // booking-settings dirty/save-all flow).
  const handleToggleTypeFlag = async (type, field) => {
    if (togglingTypeId) return;
    setTogglingTypeId(type.id);
    try {
      await updateCategory({
        clinicId,
        categoryId: type.id,
        categoryData: { [field]: !type[field] },
        therapistId,
        therapistName,
      }).unwrap();
    } catch (error) {
      console.error("[CK-3009] Error toggling treatment type booking flag:", {
        errorCode: "CK-3009",
        errorMessage: error.message,
        clinicId,
        categoryId: type.id,
        field,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בעדכון סוג הטיפול");
    } finally {
      setTogglingTypeId(null);
    }
  };

  // The therapist whose availability is being edited (self by default).
  const effectiveUid = targetTherapistId || user?.uid;

  const [updateBookingSettings] = useUpdateBookingSettingsMutation();
  const [updateSmsSettings] = useUpdateSmsSettingsMutation();
  const [updateEndMeetingSms] = useUpdateEndMeetingSmsMutation();
  const [updateShowAddressInApprovalLink] =
    useUpdateShowAddressInApprovalLinkMutation();
  const [generateBookingSlug, { isLoading: isGeneratingSlug }] =
    useGenerateBookingSlugMutation();
  const [saveWeeklyAvailability] = useSaveWeeklyAvailabilityMutation();
  const [saveAvailabilityTemplate, { isLoading: isSavingTemplate }] =
    useSaveAvailabilityTemplateMutation();

  // Fetch therapist SMS settings
  const { data: therapistSmsSettings } = useGetSmsSettingsQuery(
    { therapistId: user?.uid },
    { skip: !user?.uid || availabilityOnly },
  );

  // Week navigation: always starts at the current week (offset 0), can move
  // forward up to MAX_WEEK_OFFSET weeks to set availability ahead of time.
  const [weekOffset, setWeekOffset] = useState(0);

  const viewedWeekStart = useMemo(
    () => getWeekStart(addWeeks(new Date(), weekOffset)),
    [weekOffset],
  );
  const viewedWeekEnd = useMemo(
    () => getWeekEnd(addWeeks(new Date(), weekOffset)),
    [weekOffset],
  );
  const viewedWeekId = useMemo(
    () => getWeekId(viewedWeekStart),
    [viewedWeekStart],
  );

  // Fetch existing availability for the viewed week. RTK Query caches per
  // weekId, so weeks already visited render instantly when navigating back.
  const { data: existingAvailability, isFetching: isFetchingWeek } =
    useGetWeeklyAvailabilityQuery(
      { therapistId: effectiveUid, weekId: viewedWeekId },
      { skip: !effectiveUid },
    );

  // Fetch saved template
  const { data: savedTemplate } = useGetAvailabilityTemplateQuery(
    { therapistId: effectiveUid },
    { skip: !effectiveUid },
  );

  // Generate days for the viewed week
  const weekDays = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(viewedWeekStart);
      date.setDate(viewedWeekStart.getDate() + i);
      const isPast = date < today;

      days.push({
        date: formatLocalDateString(date),
        dayIndex: date.getDay(),
        dayLabel: HEBREW_DAYS_WITH_APOSTROPHE[date.getDay()],
        dateLabel: `${date.getDate()}/${date.getMonth() + 1}`,
        isPast,
        weekId: viewedWeekId,
      });
    }

    return days;
  }, [viewedWeekStart, viewedWeekId]);

  // Per-week local edits, keyed by weekId. Weeks are hydrated lazily as the
  // therapist navigates; dirtyWeeks tracks which weeks have unsaved changes.
  const [weeksData, setWeeksData] = useState({});
  const [dirtyWeeks, setDirtyWeeks] = useState({});

  // Each day entry is { slots: [{start,end}], blocked: [{start,end}] }.
  const EMPTY_DAY = { slots: [], blocked: [] };

  const viewedAvailability = weeksData[viewedWeekId] || {};

  const activeDaysCount = weekDays.filter(
    (day) => (viewedAvailability[day.date]?.slots || []).length > 0,
  ).length;

  // Which domain panel is visible, and per-domain unsaved-changes flags.
  const [activeDomain, setActiveDomain] = useState("availability");
  const [dirty, setDirty] = useState({
    availability: false,
    booking: false,
    sms: false,
    endMeetingSms: false,
    addressInLink: false,
  });
  const [isSavingAll, setIsSavingAll] = useState(false);

  // Feature-discovery signal: which settings domains this therapist ever
  // opened. Deduped per mount so re-renders don't inflate the telemetry
  // buffer, and skipped in the admin flow (availabilityOnly / editing another
  // therapist) so a view is never attributed to the wrong person.
  const trackedDomainsRef = useRef(new Set());
  useEffect(() => {
    if (availabilityOnly || targetTherapistId) return;
    const viewKey = DOMAIN_VIEW_KEYS[activeDomain];
    if (!viewKey || trackedDomainsRef.current.has(viewKey)) return;
    trackedDomainsRef.current.add(viewKey);
    trackView(viewKey);
  }, [activeDomain, availabilityOnly, targetTherapistId]);

  const markDirty = (domain) =>
    setDirty((prev) => (prev[domain] ? prev : { ...prev, [domain]: true }));

  const anyDirty =
    dirty.availability ||
    dirty.booking ||
    dirty.sms ||
    dirty.endMeetingSms ||
    dirty.addressInLink;

  // Hydrate the fetched week into the local cache. Keyed by the result's own
  // weekId (not the viewed one) so fast navigation can't mis-assign data, and
  // skipped for weeks that already have local (possibly dirty) edits so a
  // refetch never clobbers in-memory changes.
  useEffect(() => {
    if (!existingAvailability?.weekId) return;
    const { weekId, slots } = existingAvailability;
    setWeeksData((prev) => {
      if (prev[weekId] !== undefined || dirtyWeeks[weekId]) return prev;
      const slotsMap = {};
      (slots || []).forEach((daySlot) => {
        slotsMap[daySlot.date] = {
          slots: daySlot.slots || [],
          blocked: daySlot.blocked || [],
        };
      });
      return { ...prev, [weekId]: slotsMap };
    });
  }, [existingAvailability, dirtyWeeks]);

  const [bookingSettings, setBookingSettings] = useState({
    enabled: clinicProfile?.bookingSettings?.enabled || false,
    minHoursAhead: clinicProfile?.bookingSettings?.minHoursAhead || 24,
    maxDaysAhead: clinicProfile?.bookingSettings?.maxDaysAhead || 30,
    defaultDuration: clinicProfile?.bookingSettings?.defaultDuration || 45,
    breakBetween: clinicProfile?.bookingSettings?.breakBetween || 15,
  });

  // Wrapped booking setter — flags the booking domain dirty on every edit.
  const updateBooking = (patch) => {
    setBookingSettings((prev) => ({ ...prev, ...patch }));
    markDirty("booking");
  };

  // The enable toggle saves immediately (not via save-all); this tracks its
  // in-flight state so the switch can show a spinner / stay disabled.
  const [isTogglingBooking, setIsTogglingBooking] = useState(false);

  // Keep local booking settings in sync when the clinic profile changes in
  // Redux (e.g. after a save dispatches setClinicProfile, or auth reloads it).
  // Without this, the modal would keep showing stale local state.
  useEffect(() => {
    if (clinicProfile?.bookingSettings) {
      setBookingSettings({
        enabled: clinicProfile.bookingSettings.enabled || false,
        minHoursAhead: clinicProfile.bookingSettings.minHoursAhead || 24,
        maxDaysAhead: clinicProfile.bookingSettings.maxDaysAhead || 30,
        defaultDuration: clinicProfile.bookingSettings.defaultDuration || 45,
        breakBetween: clinicProfile.bookingSettings.breakBetween || 15,
      });
    }
  }, [clinicProfile]);

  // SMS Settings - stored on therapist document
  const [smsSettings, setSmsSettings] = useState({
    sendOnCreate: true,
    initialTiming: "immediate",
    initialHoursBefore: 3,
    sendReminderEnabled: true,
    reminderHoursBefore: 2,
    cancelRestrictionEnabled: false,
    cancelRestrictionHoursBefore: 24,
    includeTreatmentTypeInSms: false,
    skipShabbatAndHolidays: false,
  });

  // Wrapped SMS setter — flags the SMS domain dirty on every edit.
  const updateSms = (patch) => {
    setSmsSettings((prev) => ({ ...prev, ...patch }));
    markDirty("sms");
  };

  // Load therapist SMS settings when available
  useEffect(() => {
    if (therapistSmsSettings) {
      setSmsSettings({
        sendOnCreate: therapistSmsSettings.sendOnCreate ?? true,
        initialTiming: therapistSmsSettings.initialTiming ?? "immediate",
        initialHoursBefore: therapistSmsSettings.initialHoursBefore ?? 3,
        sendReminderEnabled: therapistSmsSettings.sendReminderEnabled ?? true,
        reminderHoursBefore: therapistSmsSettings.reminderHoursBefore ?? 2,
        cancelRestrictionEnabled:
          therapistSmsSettings.cancelRestrictionEnabled ?? false,
        cancelRestrictionHoursBefore:
          therapistSmsSettings.cancelRestrictionHoursBefore ?? 24,
        includeTreatmentTypeInSms:
          therapistSmsSettings.includeTreatmentTypeInSms ?? false,
        skipShabbatAndHolidays:
          therapistSmsSettings.skipShabbatAndHolidays ?? false,
      });
    }
  }, [therapistSmsSettings]);

  // Fixed message template (read-only)
  const messageTemplate =
    "היי {name}, נקבעה לך פגישה ב-{date} בשעה {time}. אנא אשר/י הגעה בקישור המצורף.";

  // ===== End-meeting (post-session) SMS — clinic-level, admin-only =====
  // Default message text per type. Used to prefill when switching type (only
  // when the current text is empty or still equals another preset, so custom
  // text is never clobbered).
  const END_MEETING_PRESETS = {
    payment: "היי {name}, תודה על הפגישה! לתשלום נוח בקישור: {link}",
    simple: "היי {name}, תודה על הפגישה שלנו היום! נתראה בפעם הבאה 🙂",
    rateus: "היי {name}, תודה על הפגישה! נשמח אם תדרג/י אותנו כאן: {link}",
  };
  const END_MEETING_PRESET_VALUES = Object.values(END_MEETING_PRESETS);

  const [endMeetingSms, setEndMeetingSms] = useState({
    enabled: false,
    type: "payment",
    messageText: END_MEETING_PRESETS.payment,
    linkUrl: "",
    delayHours: 1,
  });

  // Load clinic-level end-meeting settings from the clinic profile.
  useEffect(() => {
    const cfg = clinicProfile?.endMeetingSms;
    if (cfg) {
      setEndMeetingSms({
        enabled: cfg.enabled === true,
        type: cfg.type ?? "payment",
        messageText:
          cfg.messageText ?? END_MEETING_PRESETS[cfg.type ?? "payment"],
        linkUrl: cfg.linkUrl ?? "",
        delayHours:
          typeof cfg.delayHours === "number" ? cfg.delayHours : 1,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicProfile]);

  // Clinic-level: show the clinic/room address on the public approval
  // (confirmation) page and the booking page. Absent field = true.
  const [showAddressInLink, setShowAddressInLink] = useState(true);

  useEffect(() => {
    setShowAddressInLink(clinicProfile?.showAddressInApprovalLink !== false);
  }, [clinicProfile]);

  // Wrapped setter — flags the end-meeting domain dirty on every edit.
  const updateEndMeeting = (patch) => {
    setEndMeetingSms((prev) => ({ ...prev, ...patch }));
    markDirty("endMeetingSms");
  };

  // Switch message type; prefill the preset text unless the therapist has
  // typed their own (i.e. current text isn't empty and isn't another preset).
  const changeEndMeetingType = (type) => {
    setEndMeetingSms((prev) => {
      const isPreset =
        !prev.messageText ||
        END_MEETING_PRESET_VALUES.includes(prev.messageText.trim());
      return {
        ...prev,
        type,
        messageText: isPreset ? END_MEETING_PRESETS[type] : prev.messageText,
      };
    });
    markDirty("endMeetingSms");
  };

  // Live preview with tokens resolved. {link} uses the REAL configured URL so
  // the length check reflects the actual SMS; {name} uses a representative
  // sample name (patient names are short, the link is the variable-length risk).
  const END_MEETING_MAX = 201;
  const END_MEETING_SAMPLE_NAME = "דנה";
  const endMeetingPreview = (endMeetingSms.messageText || "")
    .replace(/\{name\}/g, END_MEETING_SAMPLE_NAME)
    .replace(/\{link\}/g, endMeetingSms.linkUrl || "https://...");
  // Resolved length of what would actually be sent. When over the limit the
  // server would truncate (cutting real content), so block it here instead.
  const endMeetingResolvedLength = endMeetingPreview.length;
  const endMeetingTooLong =
    endMeetingSms.enabled && endMeetingResolvedLength > END_MEETING_MAX;

  const [copied, setCopied] = useState(false);
  const bookingSlug = clinicProfile?.bookingSlug;
  const bookingUrl = bookingSlug
    ? `https://clinikit.co.il/book/${bookingSlug}`
    : null;

  // All window mutations target the viewed week and mark it dirty.
  // kind is 'slots' (availability window) or 'blocked' (blocked time).
  const updateDayField = (dateStr, kind, updater) => {
    setWeeksData((prev) => {
      const week = prev[viewedWeekId] || {};
      const day = week[dateStr] || EMPTY_DAY;
      return {
        ...prev,
        [viewedWeekId]: {
          ...week,
          [dateStr]: { ...day, [kind]: updater(day[kind] || []) },
        },
      };
    });
    setDirtyWeeks((prev) =>
      prev[viewedWeekId] ? prev : { ...prev, [viewedWeekId]: true },
    );
    markDirty("availability");
  };

  const addSlot = (dateStr, kind = "slots") => {
    updateDayField(dateStr, kind, (windows) => [
      ...windows,
      kind === "blocked"
        ? { start: "12:00", end: "13:00" }
        : { start: "09:00", end: "17:00" },
    ]);
  };

  // Remove a time window from a day
  const removeSlot = (dateStr, index, kind = "slots") => {
    updateDayField(dateStr, kind, (windows) =>
      windows.filter((_, i) => i !== index),
    );
  };

  // Update a time window
  const updateSlot = (dateStr, index, field, value, kind = "slots") => {
    updateDayField(dateStr, kind, (windows) =>
      windows.map((slot, i) =>
        i === index ? { ...slot, [field]: value } : slot,
      ),
    );
  };

  // Save weekly availability. Returns true on success so the save-all
  // orchestrator can report an overall result.
  const handleSaveAvailability = async () => {
    const dirtyWeekIds = Object.keys(dirtyWeeks);
    let currentWeekId = null;
    try {
      for (const weekId of dirtyWeekIds) {
        currentWeekId = weekId;
        const weekSlots = Object.entries(weeksData[weekId] || {})
          .filter(
            ([_, day]) =>
              (day?.slots || []).length > 0 || (day?.blocked || []).length > 0,
          )
          .map(([date, day]) => ({
            date,
            slots: day.slots || [],
            blocked: day.blocked || [],
          }));

        await saveWeeklyAvailability({
          therapistId: effectiveUid,
          weekId,
          slots: weekSlots,
          clinicId,
        }).unwrap();

        // Clear each week's dirty flag as soon as it lands, so a failure on
        // a later week doesn't re-save weeks that already succeeded.
        setDirtyWeeks((prev) => {
          const next = { ...prev };
          delete next[weekId];
          return next;
        });
      }

      setDirty((prev) => ({ ...prev, availability: false }));
      return true;
    } catch (error) {
      console.error("[CK-3004] Error saving availability:", {
        errorCode: "CK-3004",
        errorMessage: error.message,
        therapistId: effectiveUid,
        weekId: currentWeekId,
        dirtyWeekIds,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת הזמינות השבועית");
      return false;
    }
  };

  // Save current setup as template
  const handleSaveAsTemplate = async () => {
    try {
      const template = {};
      weekDays.forEach((day) => {
        const dayKey = DAY_KEYS[day.dayIndex];
        const dayData = viewedAvailability[day.date] || EMPTY_DAY;
        template[dayKey] = {
          slots: dayData.slots || [],
          blocked: dayData.blocked || [],
        };
      });

      await saveAvailabilityTemplate({
        therapistId: effectiveUid,
        template,
      }).unwrap();

      showSuccess("התבנית נשמרה בהצלחה");
    } catch (error) {
      console.error("[CK-3005] Error saving availability template:", {
        errorCode: "CK-3005",
        errorMessage: error.message,
        therapistId: effectiveUid,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת התבנית");
    }
  };

  // Load from template into the viewed week (skipping days already past)
  const handleLoadTemplate = () => {
    if (!savedTemplate) return;

    const newAvailability = {};
    weekDays.forEach((day) => {
      if (day.isPast) return;
      const dayKey = DAY_KEYS[day.dayIndex];
      // Template is normalized on read to { slots, blocked }.
      const t = savedTemplate[dayKey];
      if ((t?.slots || []).length > 0 || (t?.blocked || []).length > 0) {
        newAvailability[day.date] = {
          slots: [...(t.slots || [])],
          blocked: [...(t.blocked || [])],
        };
      }
    });
    setWeeksData((prev) => ({ ...prev, [viewedWeekId]: newAvailability }));
    setDirtyWeeks((prev) =>
      prev[viewedWeekId] ? prev : { ...prev, [viewedWeekId]: true },
    );
    markDirty("availability");
  };

  const handleSaveBookingSettings = async () => {
    try {
      await updateBookingSettings({ clinicId, bookingSettings }).unwrap();
      // Keep Redux in sync so the values persist when the modal reopens.
      dispatch(setClinicProfile({ ...clinicProfile, bookingSettings }));
      setDirty((prev) => ({ ...prev, booking: false }));
      return true;
    } catch (error) {
      console.error("[CK-3006] Error saving booking settings:", {
        errorCode: "CK-3006",
        errorMessage: error.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת הגדרות ההזמנה");
      return false;
    }
  };

  // The enable toggle saves immediately (optimistic) and syncs Redux, so the
  // switch state persists without a refresh and never silently reverts.
  const handleToggleEnabled = async () => {
    if (isTogglingBooking) return;
    const next = { ...bookingSettings, enabled: !bookingSettings.enabled };
    setBookingSettings(next);
    setIsTogglingBooking(true);
    try {
      await updateBookingSettings({ clinicId, bookingSettings: next }).unwrap();
      dispatch(
        setClinicProfile({ ...clinicProfile, bookingSettings: next }),
      );
    } catch (error) {
      console.error("[CK-3006] Error toggling booking enabled:", {
        errorCode: "CK-3006",
        errorMessage: error.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת הגדרות ההזמנה");
      // Revert the optimistic change on failure.
      setBookingSettings((prev) => ({ ...prev, enabled: !next.enabled }));
    } finally {
      setIsTogglingBooking(false);
    }
  };

  const handleGenerateSlug = async () => {
    try {
      const { slug } = await generateBookingSlug({ clinicId }).unwrap();
      // Push the new slug into Redux so the link URL appears immediately.
      dispatch(setClinicProfile({ ...clinicProfile, bookingSlug: slug }));
      showSuccess("קישור ההזמנה נוצר");
    } catch (error) {
      console.error("[CK-3007] Error generating booking slug:", {
        errorCode: "CK-3007",
        errorMessage: error.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה ביצירת קישור ההזמנה");
    }
  };

  const handleSaveSmsSettings = async () => {
    try {
      await updateSmsSettings({ therapistId: user?.uid, smsSettings }).unwrap();
      setDirty((prev) => ({ ...prev, sms: false }));
      return true;
    } catch (error) {
      console.error("[CK-3008] Error saving SMS settings:", {
        errorCode: "CK-3008",
        errorMessage: error.message,
        therapistId: user?.uid,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת הגדרות ה-SMS");
      return false;
    }
  };

  // Save clinic-level end-meeting SMS settings (admin only). Syncs Redux so the
  // values persist when the modal reopens and the appointment checkbox reflects
  // the current enabled state.
  const handleSaveEndMeetingSms = async () => {
    // Block saving an over-length message — the server would otherwise truncate
    // real content (name/link). Alert and stop so nothing broken gets saved.
    if (endMeetingTooLong) {
      showError(
        `הודעת סיום הפגישה חורגת מ-${END_MEETING_MAX} תווים. קצר/י את הטקסט או השתמש/י בקישור קצר יותר.`,
      );
      return false;
    }
    try {
      const { endMeetingSms: saved } = await updateEndMeetingSms({
        clinicId,
        endMeetingSms,
      }).unwrap();
      dispatch(
        setClinicProfile({ ...clinicProfile, endMeetingSms: saved }),
      );
      setDirty((prev) => ({ ...prev, endMeetingSms: false }));
      return true;
    } catch (error) {
      console.error("[CK-5361] Error saving end-meeting SMS settings:", {
        errorCode: "CK-5361",
        errorMessage: error.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת הגדרות הודעת סיום הפגישה");
      return false;
    }
  };

  // Save clinic-level show-address-in-approval-link preference (admin only).
  const handleSaveAddressInLink = async () => {
    try {
      const { showAddressInApprovalLink: saved } =
        await updateShowAddressInApprovalLink({
          clinicId,
          show: showAddressInLink,
        }).unwrap();
      dispatch(
        setClinicProfile({
          ...clinicProfile,
          showAddressInApprovalLink: saved,
        }),
      );
      setDirty((prev) => ({ ...prev, addressInLink: false }));
      return true;
    } catch (error) {
      console.error("[CK-5363] Error saving show-address-in-approval-link:", {
        errorCode: "CK-5363",
        errorMessage: error.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת הגדרת הצגת הכתובת");
      return false;
    }
  };

  // Single sticky-footer save — runs the relevant mutation for every dirty
  // domain (each keeps its own CK error code + error toast) and shows one
  // success toast when everything saved cleanly.
  const handleSaveAll = async () => {
    if (!anyDirty || isSavingAll) return;
    setIsSavingAll(true);
    let allOk = true;
    try {
      if (dirty.availability) {
        allOk = (await handleSaveAvailability()) && allOk;
      }
      if (dirty.booking && !availabilityOnly) {
        allOk = (await handleSaveBookingSettings()) && allOk;
      }
      if (dirty.sms && !availabilityOnly) {
        allOk = (await handleSaveSmsSettings()) && allOk;
      }
      if (dirty.endMeetingSms && !availabilityOnly) {
        allOk = (await handleSaveEndMeetingSms()) && allOk;
      }
      if (dirty.addressInLink && !availabilityOnly) {
        allOk = (await handleSaveAddressInLink()) && allOk;
      }
    } finally {
      setIsSavingAll(false);
    }
    if (allOk) showSuccess("ההגדרות נשמרו בהצלחה");
  };

  const copyToClipboard = () => {
    if (bookingUrl) {
      navigator.clipboard.writeText(bookingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Renders one time window (availability or blocked) as an inline range
  // editor. Blocked windows get the red `blocked` class.
  const renderWindow = (day, slot, index, kind) => (
    <div
      key={`${kind}-${index}`}
      className={`cset-window ${kind === "blocked" ? "blocked" : ""}`}
    >
      <span className="cset-window-label">מ-</span>
      <TimePicker
        className="cset-tp"
        value={slot.start}
        onChange={(e) =>
          updateSlot(day.date, index, "start", e.target.value, kind)
        }
      />
      <span className="cset-window-sep">–</span>
      <span className="cset-window-label">עד</span>
      <TimePicker
        className="cset-tp"
        value={slot.end}
        onChange={(e) =>
          updateSlot(day.date, index, "end", e.target.value, kind)
        }
      />
      <button
        className="cset-window-remove"
        onClick={() => removeSlot(day.date, index, kind)}
        title={kind === "blocked" ? "מחק זמן חסום" : "מחק חלון זמן"}
        aria-label={kind === "blocked" ? "מחק זמן חסום" : "מחק חלון זמן"}
      >
        <Trash size={13} weight="bold" />
      </button>
    </div>
  );

  // Renders a single day as a compact row: day label on the right, its time
  // windows (availability + blocked) as inline range editors that wrap, plus
  // "add window" and red "add blocked time" buttons.
  const renderAvailabilityDay = (day) => {
    const dayData = viewedAvailability[day.date] || EMPTY_DAY;
    const slots = dayData.slots || [];
    const blocked = dayData.blocked || [];
    const hasSlots = slots.length > 0;

    return (
      <div
        key={day.date}
        className={`cset-day-row ${day.isPast ? "past" : ""} ${
          hasSlots ? "active" : ""
        }`}
      >
        <div className="cset-day-label">
          {hasSlots && <span className="cset-day-dot" />}
          <span className="cset-day-name">{day.dayLabel}</span>
          <span className="cset-day-date">{day.dateLabel}</span>
        </div>

        {day.isPast ? (
          <span className="cset-day-past">יום שעבר</span>
        ) : (
          <div className="cset-day-windows">
            {slots.length === 0 && blocked.length === 0 && (
              <span className="cset-day-empty">אין זמינות</span>
            )}

            {slots.map((slot, index) =>
              renderWindow(day, slot, index, "slots"),
            )}
            {blocked.map((slot, index) =>
              renderWindow(day, slot, index, "blocked"),
            )}

            <button
              className="cset-window-add"
              onClick={() => addSlot(day.date)}
            >
              <Plus size={13} weight="bold" />
              <span>חלון</span>
            </button>
            <button
              className="cset-window-add blocked"
              onClick={() => addSlot(day.date, "blocked")}
              title="הוסף זמן חסום"
            >
              <Prohibit size={13} weight="bold" />
              <span>חסימה</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── Domain panels ──────────────────────────────────────────────────────

  const renderAvailabilityPanel = () => (
    <div className="cset-card">
      <div className="cset-card-header">
        <span className="cset-card-icon green">
          <CalendarBlank size={20} weight="fill" />
        </span>
        <div className="cset-card-heading">
          <h3>זמינות שבועית</h3>
          <p>
            {availabilityOnly
              ? "הגדר מתי ניתן לקבוע פגישות עם המטפל/ת, וסמן זמנים חסומים (חופשה, הפסקה) שיופיעו באדום ביומן."
              : "הגדר מתי מטופלים יכולים לקבוע איתך פגישות, וסמן זמנים חסומים (חופשה, הפסקה) שיופיעו באדום ביומן."}
            {savedTemplate
              ? " טען את הלו״ז הקבוע שלך כתבנית."
              : " שמור כתבנית כדי לטעון אותן שעות בשבועות הבאים."}
          </p>
        </div>
      </div>

      <div className="cset-toolbar">
        {savedTemplate && (
          <button
            className="cset-btn-ghost"
            onClick={handleLoadTemplate}
            title="טען את התבנית השמורה לכל הימים הפתוחים"
          >
            <DownloadSimple size={15} weight="bold" />
            <span>טען תבנית</span>
          </button>
        )}
        <button
          className="cset-btn-ghost"
          onClick={handleSaveAsTemplate}
          disabled={isSavingTemplate}
          title="שמור את הלו״ז הנוכחי כתבנית לשימוש חוזר"
        >
          <FloppyDisk size={15} weight="bold" />
          <span>{isSavingTemplate ? "שומר..." : "שמור כתבנית"}</span>
        </button>
      </div>

      <div className="cset-week">
        <div className="cset-week-nav">
          <button
            className="cset-week-nav-btn"
            onClick={() => setWeekOffset((o) => Math.max(0, o - 1))}
            disabled={weekOffset === 0}
            title="שבוע קודם"
            aria-label="שבוע קודם"
          >
            <CaretRight size={16} weight="bold" />
          </button>

          <div className="cset-week-title">
            <span>
              {weekOffset === 0
                ? "שבוע נוכחי"
                : weekOffset === 1
                  ? "שבוע הבא"
                  : `בעוד ${weekOffset} שבועות`}
              {isFetchingWeek && (
                <LoaderLoop size="xs" />
              )}
            </span>
            <span className="cset-week-range">
              {viewedWeekStart.toLocaleDateString("he-IL")} –{" "}
              {viewedWeekEnd.toLocaleDateString("he-IL")}
            </span>
            <span className="cset-week-count">
              {activeDaysCount === 0
                ? "אין ימים עם זמינות"
                : activeDaysCount === 1
                  ? "יום אחד עם זמינות"
                  : `${activeDaysCount} ימים עם זמינות`}
            </span>
          </div>

          <button
            className="cset-week-nav-btn"
            onClick={() =>
              setWeekOffset((o) => Math.min(MAX_WEEK_OFFSET, o + 1))
            }
            disabled={weekOffset === MAX_WEEK_OFFSET}
            title="שבוע הבא"
            aria-label="שבוע הבא"
          >
            <CaretLeft size={16} weight="bold" />
          </button>
        </div>
        <div className="cset-day-list">
          {weekDays.map((day) => renderAvailabilityDay(day))}
        </div>
      </div>
    </div>
  );

  // Self-booking v1 is solo-clinic only — the server refuses multi-therapist
  // clinics (bookingHelper getSoloTherapist), so don't let admins enable a
  // link that will dead-end for patients.
  const isMultiTherapistClinic = clinicProfile?.isSoloClinic === false;

  const renderBookingPanel = () => (
    <div className="cset-card">
      <div className="cset-card-header">
        <span className="cset-card-icon blue">
          <LinkIcon size={20} weight="fill" />
        </span>
        <div className="cset-card-heading">
          <h3>קישור הזמנה עצמית</h3>
          <p>
            שתף קישור אישי שבו מטופלים קובעים פגישות בעצמם — בהתאם לחלונות
            הזמן שהגדרת בזמינות השבועית.
          </p>
        </div>
        {!isMultiTherapistClinic && (
          <span
            className={`cset-status ${bookingSettings.enabled ? "on" : "off"}`}
          >
            <span className="cset-status-dot" />
            {bookingSettings.enabled ? "פעיל" : "כבוי"}
          </span>
        )}
      </div>

      {isMultiTherapistClinic ? (
        <div className="cset-booking-locked">
          <span className="cset-booking-locked-icon">
            <UsersThree size={26} weight="duotone" />
          </span>
          <h4>בקרוב גם לקליניקות מרובות מטפלים</h4>
          <p>
            בשלב זה, הזמנת תורים עצמאית זמינה לקליניקות עם מטפל אחד בלבד.
            אנחנו עובדים על תמיכה בקליניקות עם מספר מטפלים — היא תתווסף
            בקרוב.
          </p>
        </div>
      ) : (
        renderBookingControls()
      )}
    </div>
  );

  const renderBookingControls = () => (
    <>
      <button
        type="button"
        className={`cset-toggle-row ${bookingSettings.enabled ? "on" : ""}`}
        onClick={handleToggleEnabled}
        disabled={isTogglingBooking}
      >
        <div className="cset-toggle-text">
          <span className="cset-toggle-title">
            אפשר למטופלים לקבוע פגישות בעצמם
          </span>
          <span className="cset-toggle-desc">
            כשכבוי, הקישור אינו פעיל ומטופלים לא יכולים לקבוע אליך.
          </span>
        </div>
        {isTogglingBooking ? (
          <LoaderLoop size="xs" />
        ) : (
          <span
            className={`cset-switch ${bookingSettings.enabled ? "active" : ""}`}
          >
            <span className="cset-switch-knob" />
          </span>
        )}
      </button>

      {bookingSettings.enabled && (
        <>
          <div className="cset-field">
            <span className="cset-label">הקישור שלך</span>
            {bookingUrl ? (
              <div className="cset-link-row">
                <input
                  type="text"
                  value={bookingUrl}
                  readOnly
                  className="cset-input"
                  onClick={(e) => e.target.select()}
                />
                <button
                  className={`cset-copy-btn ${copied ? "copied" : ""}`}
                  onClick={copyToClipboard}
                  title={copied ? "הועתק!" : "העתק קישור"}
                >
                  {copied ? (
                    <Check size={17} weight="bold" />
                  ) : (
                    <Copy size={17} weight="regular" />
                  )}
                  <span>{copied ? "הועתק" : "העתק"}</span>
                </button>
              </div>
            ) : (
              <button
                className="cset-btn-primary"
                onClick={handleGenerateSlug}
                disabled={isGeneratingSlug}
              >
                {isGeneratingSlug ? (
                  <>
                    <LoaderLoop size="xs" />
                    <span>יוצר...</span>
                  </>
                ) : (
                  "צור קישור הזמנה"
                )}
              </button>
            )}
          </div>

          <div className="cset-subsection">
            <h4 className="cset-subtitle">מגבלות הזמנה</h4>
            <div className="cset-fields-grid">
              <label className="cset-field">
                <span className="cset-label">זמן מינימלי לפני פגישה</span>
                <div className="cset-input-unit">
                  <input
                    type="number"
                    className="cset-input"
                    value={bookingSettings.minHoursAhead}
                    onChange={(e) =>
                      updateBooking({
                        minHoursAhead: parseInt(e.target.value, 10),
                      })
                    }
                    min="1"
                  />
                  <span className="cset-unit">שעות</span>
                </div>
                <span className="cset-hint">מונע הזמנות של הרגע האחרון</span>
              </label>

              <label className="cset-field">
                <span className="cset-label">הזמנה מקסימלית קדימה</span>
                <div className="cset-input-unit">
                  <input
                    type="number"
                    className="cset-input"
                    value={bookingSettings.maxDaysAhead}
                    onChange={(e) =>
                      updateBooking({
                        maxDaysAhead: parseInt(e.target.value, 10),
                      })
                    }
                    min="1"
                  />
                  <span className="cset-unit">ימים</span>
                </div>
                <span className="cset-hint">עד כמה ימים קדימה אפשר לקבוע</span>
              </label>

              <label className="cset-field">
                <span className="cset-label">משך פגישה ברירת מחדל</span>
                <div className="cset-input-unit">
                  <input
                    type="number"
                    className="cset-input"
                    value={bookingSettings.defaultDuration}
                    onChange={(e) =>
                      updateBooking({
                        defaultDuration: parseInt(e.target.value, 10),
                      })
                    }
                    min="1"
                    step="1"
                  />
                  <span className="cset-unit">דקות</span>
                </div>
                <span className="cset-hint">
                  אורך פגישה שמוצע למטופל כשאין שירותים ציבוריים
                </span>
              </label>
            </div>
          </div>

          {renderBookingServices()}

          <p className="cset-note">
            <Info size={15} weight="fill" />
            <span>
              מטופלים יראו רק את החלונות הפנויים שהגדרת בזמינות השבועית.
            </span>
          </p>
        </>
      )}
    </>
  );

  // Services list inside the booking panel: every treatment type, with
  // inline public / show-duration / show-price switches. Public types with a
  // duration are what patients pick on /book/:slug.
  const renderBookingServices = () => {
    const publicCount = treatmentTypes.filter((t) => t.isPublic).length;
    return (
      <div className="cset-subsection">
        <div className="cset-services-head">
          <div>
            <h4 className="cset-subtitle">שירותים בעמוד ההזמנה</h4>
            <p className="cset-services-desc">
              סמן אילו סוגי טיפול מטופלים יכולים לבחור בקישור. המועדים הפנויים
              יחושבו לפי משך השירות.
            </p>
          </div>
          <button
            type="button"
            className="cset-btn-ghost"
            onClick={() => setTreatmentTypeModal({ editing: null })}
          >
            <Plus size={15} weight="bold" />
            <span>הוסף סוג טיפול</span>
          </button>
        </div>

        {isLoadingTreatmentTypes ? (
          <ContentLoader size="sm" compact />
        ) : treatmentTypes.length === 0 ? (
          <div className="cset-services-empty">
            <Stethoscope size={22} weight="duotone" />
            <span>
              עדיין אין סוגי טיפול. הוסף סוג טיפול עם משך כדי שמטופלים יוכלו
              לבחור שירות.
            </span>
          </div>
        ) : (
          <div className="cset-services-list">
            {treatmentTypes.map((type) => {
              const warnings = getPublicTypeWarnings(type);
              const busy = togglingTypeId === type.id;
              return (
                <div
                  key={type.id}
                  className={`cset-service-row ${type.isPublic ? "public" : ""}`}
                >
                  <span
                    className="cset-service-dot"
                    style={{ backgroundColor: type.color }}
                  />
                  <div className="cset-service-main">
                    <span className="cset-service-name">{type.name}</span>
                    <span className="cset-service-meta">
                      {type.duration ? `${type.duration} דק׳` : "ללא משך"}
                      {type.rate != null ? ` · ₪${type.rate}` : ""}
                    </span>
                  </div>
                  {warnings.length > 0 && (
                    <span className="cset-service-warn" title={warnings.join(" · ")}>
                      <WarningCircle size={16} weight="fill" />
                    </span>
                  )}
                  <div className="cset-service-switches">
                    <button
                      type="button"
                      className="cset-mini-toggle"
                      onClick={() => handleToggleTypeFlag(type, "isPublic")}
                      disabled={busy}
                      aria-pressed={type.isPublic}
                      title="הצג בעמוד ההזמנה"
                    >
                      <Globe size={13} weight={type.isPublic ? "fill" : "regular"} />
                      <span>ציבורי</span>
                      <span className={`cset-mini-switch ${type.isPublic ? "active" : ""}`}>
                        <span className="cset-mini-switch-knob" />
                      </span>
                    </button>
                    <button
                      type="button"
                      className="cset-mini-toggle"
                      onClick={() => handleToggleTypeFlag(type, "showDuration")}
                      disabled={busy || !type.isPublic}
                      aria-pressed={type.showDuration}
                      title="הצג משך למטופל"
                    >
                      <span>משך</span>
                      <span className={`cset-mini-switch ${type.showDuration ? "active" : ""}`}>
                        <span className="cset-mini-switch-knob" />
                      </span>
                    </button>
                    <button
                      type="button"
                      className="cset-mini-toggle"
                      onClick={() => handleToggleTypeFlag(type, "showPrice")}
                      disabled={busy || !type.isPublic}
                      aria-pressed={type.showPrice}
                      title="הצג מחיר למטופל"
                    >
                      <span>מחיר</span>
                      <span className={`cset-mini-switch ${type.showPrice ? "active" : ""}`}>
                        <span className="cset-mini-switch-knob" />
                      </span>
                    </button>
                  </div>
                  <button
                    type="button"
                    className="cset-service-edit"
                    onClick={() => setTreatmentTypeModal({ editing: type })}
                    title="ערוך"
                  >
                    <Pencil size={15} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {treatmentTypes.length > 0 && publicCount === 0 && (
          <p className="cset-services-note">
            כשאין שירותים ציבוריים, המטופל יראה רק את משך ברירת המחדל שהוגדר
            למעלה.
          </p>
        )}
      </div>
    );
  };

  const renderSmsPanel = () => (
    <div className="cset-card">
      <div className="cset-card-header">
        <span className="cset-card-icon purple">
          <ChatText size={20} weight="fill" />
        </span>
        <div className="cset-card-heading">
          <h3>הודעות SMS למטופלים</h3>
          <p>
            שלוט בהודעות האוטומטיות שנשלחות סביב הפגישה. כל הודעה כוללת קישור
            לאישור או ביטול הגעה.
          </p>
        </div>
      </div>

      <div className="cset-sms-list">
        {/* Confirmation + reminder — two automatic messages, one card */}
        <div
          className={`cset-sms-card ${smsSettings.sendOnCreate || smsSettings.sendReminderEnabled ? "active" : ""}`}
        >
          {/* Initial / confirmation */}
          <div
            className={`cset-sms-section ${smsSettings.sendOnCreate ? "active" : ""}`}
          >
            <button
              type="button"
              className="cset-sms-head"
              onClick={() =>
                updateSms(
                  smsSettings.sendOnCreate
                    ? { sendOnCreate: false, sendReminderEnabled: false }
                    : { sendOnCreate: true },
                )
              }
            >
              <div className="cset-toggle-text">
                <span className="cset-toggle-title">שלח הודעת אישור הגעה</span>
                <span className="cset-toggle-desc">
                  הודעה ראשונה שהמטופל מקבל עבור הפגישה, עם קישור לאישור או ביטול
                  הגעה. אפשר לשלוח אותה מיד כשהפגישה נקבעת, או לתזמן אותה מספר שעות
                  לפני מועד הפגישה. אם ההודעה מכובה — לא תישלח כלל הודעת אישור.
                </span>
              </div>
              <span
                className={`cset-switch ${smsSettings.sendOnCreate ? "active" : ""}`}
              >
                <span className="cset-switch-knob" />
              </span>
            </button>

            {smsSettings.sendOnCreate && (
              <div className="cset-sms-body">
                <div className="cset-mode">
                  <button
                    type="button"
                    className={`cset-mode-btn ${smsSettings.initialTiming !== "before" ? "active" : ""}`}
                    onClick={() => updateSms({ initialTiming: "immediate" })}
                  >
                    מיד עם קביעת הפגישה
                  </button>
                  <button
                    type="button"
                    className={`cset-mode-btn ${smsSettings.initialTiming === "before" ? "active" : ""}`}
                    onClick={() => updateSms({ initialTiming: "before" })}
                  >
                    לפני הפגישה
                  </button>
                </div>

                {smsSettings.initialTiming === "before" && (
                  <div className="cset-inline" style={{ marginTop: 12 }}>
                    <span>שלח אישור</span>
                    <input
                      type="number"
                      className="cset-input cset-input-mini"
                      min="1"
                      max="168"
                      value={smsSettings.initialHoursBefore}
                      onChange={(e) =>
                        updateSms({
                          initialHoursBefore: Math.max(
                            1,
                            Math.min(168, parseInt(e.target.value, 10) || 3),
                          ),
                        })
                      }
                    />
                    <span>שעות לפני הפגישה</span>
                  </div>
                )}
                <p className="cset-hint">
                  {smsSettings.initialTiming === "before"
                    ? "ההודעה תתוזמן אוטומטית ותישלח את מספר השעות שנבחר לפני הפגישה. בפגישה שנקבעת בטווח קצר מהזמן שנבחר — ההודעה לא תישלח."
                    : "ההודעה תישלח מיד עם שמירת הפגישה, כדי שהמטופל יאשר הגעה מוקדם ככל האפשר."}
                </p>
              </div>
            )}
          </div>

          {/* Reminder — available only when the confirmation message is on */}
          <div
            className={`cset-sms-section ${smsSettings.sendReminderEnabled ? "active" : ""} ${smsSettings.sendOnCreate ? "" : "disabled"}`}
          >
            <button
              type="button"
              className="cset-sms-head"
              disabled={!smsSettings.sendOnCreate}
              onClick={() =>
                updateSms({ sendReminderEnabled: !smsSettings.sendReminderEnabled })
              }
            >
              <div className="cset-toggle-text">
                <span className="cset-toggle-title">שלח הודעת תזכורת</span>
                <span className="cset-toggle-desc">
                  {smsSettings.sendOnCreate
                    ? "הודעה נוספת שנשלחת אוטומטית סמוך למועד הפגישה, כדי להזכיר למטופל ולהקטין אי-הגעות. גם היא כוללת קישור לאישור הגעה. אפשר לקבוע כמה שעות לפני הפגישה תישלח. אם המטופל כבר סירב באישור הראשון — התזכורת לא תישלח."
                    : "כדי לשלוח תזכורת יש להפעיל תחילה את הודעת אישור ההגעה. התזכורת מתבססת על אותו קישור אישור ואינה נשלחת בנפרד."}
                </span>
              </div>
              <span
                className={`cset-switch ${smsSettings.sendReminderEnabled ? "active" : ""}`}
              >
                <span className="cset-switch-knob" />
              </span>
            </button>

            {smsSettings.sendReminderEnabled && (
              <div className="cset-sms-body">
                <div className="cset-inline">
                  <span>שלח תזכורת</span>
                  <input
                    type="number"
                    className="cset-input cset-input-mini"
                    min="1"
                    max="48"
                    value={smsSettings.reminderHoursBefore}
                    onChange={(e) =>
                      updateSms({
                        reminderHoursBefore: Math.max(
                          1,
                          Math.min(48, parseInt(e.target.value, 10) || 2),
                        ),
                      })
                    }
                  />
                  <span>שעות לפני הפגישה</span>
                </div>
                <p className="cset-hint">
                  התזכורת תישלח את מספר השעות שנבחר לפני מועד הפגישה. מומלץ לתזמן
                  אותה קרוב לפגישה (למשל 2 שעות) כדי שתהיה רלוונטית למטופל.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Cancel restriction */}
        <div
          className={`cset-sms-card ${smsSettings.cancelRestrictionEnabled ? "active" : ""}`}
        >
          <button
            type="button"
            className="cset-sms-head"
            onClick={() =>
              updateSms({
                cancelRestrictionEnabled: !smsSettings.cancelRestrictionEnabled,
              })
            }
          >
            <div className="cset-toggle-text">
              <span className="cset-toggle-title">הגבל ביטול פגישה</span>
              <span className="cset-toggle-desc">
                מניעת ביטול פגישה דרך הקישור בסמוך למועד הפגישה
              </span>
            </div>
            <span
              className={`cset-switch ${smsSettings.cancelRestrictionEnabled ? "active" : ""}`}
            >
              <span className="cset-switch-knob" />
            </span>
          </button>

          {smsSettings.cancelRestrictionEnabled && (
            <div className="cset-sms-body">
              <div className="cset-inline">
                <span>ביטול פגישה עד</span>
                <input
                  type="number"
                  className="cset-input cset-input-mini"
                  min="1"
                  max="72"
                  value={smsSettings.cancelRestrictionHoursBefore}
                  onChange={(e) =>
                    updateSms({
                      cancelRestrictionHoursBefore: Math.max(
                        1,
                        Math.min(72, parseInt(e.target.value, 10) || 24),
                      ),
                    })
                  }
                />
                <span>שעות לפני הפגישה</span>
              </div>
              <p className="cset-hint">
                מטופל שירצה לבטל {smsSettings.cancelRestrictionHoursBefore} שעות
                ופחות לפני המועד — יופנה ליצור איתך קשר ישירות.
              </p>
            </div>
          )}
        </div>

        {/* Include treatment type */}
        <div
          className={`cset-sms-card ${smsSettings.includeTreatmentTypeInSms ? "active" : ""}`}
        >
          <button
            type="button"
            className="cset-sms-head"
            onClick={() =>
              updateSms({
                includeTreatmentTypeInSms: !smsSettings.includeTreatmentTypeInSms,
              })
            }
          >
            <div className="cset-toggle-text">
              <span className="cset-toggle-title">צרף סוג טיפול להודעה</span>
              <span className="cset-toggle-desc">
                הוספת סוג הטיפול (אם נבחר) להודעת האישור והתזכורת
              </span>
            </div>
            <span
              className={`cset-switch ${smsSettings.includeTreatmentTypeInSms ? "active" : ""}`}
            >
              <span className="cset-switch-knob" />
            </span>
          </button>

          {smsSettings.includeTreatmentTypeInSms && (
            <div className="cset-sms-body">
              <p className="cset-hint">
                סוג הטיפול יצורף רק כשנבחר בפגישה, ורק אם ההודעה לא חורגת מ-201
                תווים. אם ההודעה ארוכה מדי, סוג הטיפול יושמט ותופיע התראה בתצוגה
                המקדימה.
              </p>
            </div>
          )}
        </div>

        {/* Skip Shabbat & holidays */}
        <div
          className={`cset-sms-card ${smsSettings.skipShabbatAndHolidays ? "active" : ""}`}
        >
          <button
            type="button"
            className="cset-sms-head"
            onClick={() =>
              updateSms({
                skipShabbatAndHolidays: !smsSettings.skipShabbatAndHolidays,
              })
            }
          >
            <div className="cset-toggle-text">
              <span className="cset-toggle-title">
                אל תשלח SMS בשבת ובחגים
              </span>
              <span className="cset-toggle-desc">
                הודעות שמתוזמנות לשבת או לחג יידחו לאחרי צאת השבת/החג
              </span>
            </div>
            <span
              className={`cset-switch ${smsSettings.skipShabbatAndHolidays ? "active" : ""}`}
            >
              <span className="cset-switch-knob" />
            </span>
          </button>

          {smsSettings.skipShabbatAndHolidays && (
            <div className="cset-sms-body">
              <p className="cset-hint">
                הודעה שאמורה להישלח במהלך שבת או חג תישלח כשעתיים אחרי צאת
                השבת/החג. אם הפגישה עצמה מתקיימת בשבת או בחג, ההודעה תישלח
                לפני כניסת השבת/החג.
              </p>
            </div>
          )}
        </div>

        {/* Regular confirmation/reminder message preview */}
        <div className="cset-preview">
          <span className="cset-label">תצוגה מקדימה של ההודעה</span>
          <div className="cset-preview-bubble">
            {smsSettings.includeTreatmentTypeInSms
              ? "היי {name}, נקבעה לך פגישה ל{treatmentType} ב{clinic} ב-{date} בשעה {time}. אנא אשר/י הגעה בקישור המצורף."
              : messageTemplate}
          </div>
          <p className="cset-hint">
            שדות בסוגריים מוחלפים אוטומטית בפרטי הפגישה בפועל.
          </p>
        </div>

        {/* End-meeting (post-session) message — clinic-level, admin only.
            Placed below the regular message preview. */}
        {isAdmin && (
          <div
            className={`cset-sms-card ${endMeetingSms.enabled ? "active" : ""}`}
          >
            <button
              type="button"
              className="cset-sms-head"
              onClick={() =>
                updateEndMeeting({ enabled: !endMeetingSms.enabled })
              }
            >
              <div className="cset-toggle-text">
                <span className="cset-toggle-title">הודעת סיום פגישה</span>
                <span className="cset-toggle-desc">
                  הודעה שנשלחת אוטומטית למטופל לאחר סיום הפגישה — קישור לתשלום,
                  בקשת דירוג, או תודה. ההודעה נשלחת רק בפגישות שבהן סימנת זאת.
                  זוהי הגדרה ברמת הקליניקה.
                </span>
              </div>
              <span
                className={`cset-switch ${endMeetingSms.enabled ? "active" : ""}`}
              >
                <span className="cset-switch-knob" />
              </span>
            </button>

            {endMeetingSms.enabled && (
              <div className="cset-sms-body">
                {/* Type selector */}
                <div className="cset-mode">
                  <button
                    type="button"
                    className={`cset-mode-btn ${endMeetingSms.type === "payment" ? "active" : ""}`}
                    onClick={() => changeEndMeetingType("payment")}
                  >
                    קישור תשלום
                  </button>
                  <button
                    type="button"
                    className={`cset-mode-btn ${endMeetingSms.type === "simple" ? "active" : ""}`}
                    onClick={() => changeEndMeetingType("simple")}
                  >
                    הודעה פשוטה
                  </button>
                  <button
                    type="button"
                    className={`cset-mode-btn ${endMeetingSms.type === "rateus" ? "active" : ""}`}
                    onClick={() => changeEndMeetingType("rateus")}
                  >
                    דרגו אותנו
                  </button>
                </div>

                {/* Link URL — only for link-based types */}
                {endMeetingSms.type !== "simple" && (
                  <div className="cset-field" style={{ marginTop: 12 }}>
                    <span className="cset-label">
                      {endMeetingSms.type === "payment"
                        ? "קישור לתשלום (Bit / PayBox / העברה)"
                        : "קישור לדירוג (Google / פייסבוק)"}
                    </span>
                    <input
                      type="url"
                      dir="ltr"
                      className="cset-input"
                      placeholder="https://..."
                      value={endMeetingSms.linkUrl}
                      onChange={(e) =>
                        updateEndMeeting({ linkUrl: e.target.value })
                      }
                    />
                  </div>
                )}

                {/* Message text */}
                <div className="cset-field" style={{ marginTop: 12 }}>
                  <span className="cset-label">נוסח ההודעה</span>
                  <textarea
                    className="cset-input"
                    rows={3}
                    maxLength={201}
                    value={endMeetingSms.messageText}
                    onChange={(e) =>
                      updateEndMeeting({ messageText: e.target.value })
                    }
                  />
                  <p className="cset-hint">
                    {"{name} מוחלף בשם המטופל/ת"}
                    {endMeetingSms.type !== "simple"
                      ? ", ו-{link} מוחלף בקישור שהזנת."
                      : "."}{" "}
                    עד 201 תווים ({endMeetingSms.messageText.length}/201).
                  </p>
                </div>

                {/* Delay */}
                <div className="cset-inline" style={{ marginTop: 8 }}>
                  <span>שלח</span>
                  <input
                    type="number"
                    className="cset-input cset-input-mini"
                    min="0"
                    max="24"
                    value={endMeetingSms.delayHours}
                    onChange={(e) =>
                      updateEndMeeting({
                        delayHours: Math.max(
                          0,
                          Math.min(24, parseInt(e.target.value, 10) || 0),
                        ),
                      })
                    }
                  />
                  <span>שעות לאחר סיום הפגישה</span>
                </div>
                <p className="cset-hint">
                  {endMeetingSms.delayHours === 0
                    ? "ההודעה תישלח מיד עם סיום הפגישה."
                    : `ההודעה תישלח ${endMeetingSms.delayHours} שעות לאחר סיום הפגישה.`}
                  {endMeetingSms.type === "payment" &&
                    " לא תישלח אם הפגישה כבר סומנה כשולמה."}
                </p>

                {/* Preview + resolved-length validation. The final message
                    (name + link substituted) must fit in 201 chars. */}
                <div className="cset-preview" style={{ marginTop: 12 }}>
                  <span className="cset-label">תצוגה מקדימה</span>
                  <div
                    className="cset-preview-bubble"
                    style={
                      endMeetingTooLong
                        ? {
                            borderColor: "var(--color-error)",
                            color: "var(--color-error)",
                          }
                        : undefined
                    }
                  >
                    {endMeetingPreview}
                  </div>
                  <p
                    className="cset-hint"
                    style={
                      endMeetingTooLong
                        ? { color: "var(--color-error)", fontWeight: 600 }
                        : undefined
                    }
                  >
                    {endMeetingResolvedLength}/{END_MEETING_MAX} תווים בהודעה
                    הסופית
                    {endMeetingTooLong &&
                      ` — ההודעה חורגת מ-${END_MEETING_MAX} תווים ולא ניתן לשמור. קצר/י את הטקסט או השתמש/י בקישור קצר יותר.`}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Show clinic/room address on the public approval + booking pages —
            clinic-level, admin only. Default ON (absent field = true). */}
        {isAdmin && (
          <div
            className={`cset-sms-card ${showAddressInLink ? "active" : ""}`}
          >
            <button
              type="button"
              className="cset-sms-head"
              onClick={() => {
                setShowAddressInLink((prev) => !prev);
                markDirty("addressInLink");
              }}
            >
              <div className="cset-toggle-text">
                <span className="cset-toggle-title">
                  הצגת כתובת הקליניקה בקישור אישור הגעה
                </span>
                <span className="cset-toggle-desc">
                  כאשר כבוי, הכתובת וכפתור הניווט לא יוצגו למטופל בדף אישור
                  ההגעה ובדף קביעת התור
                </span>
              </div>
              <span
                className={`cset-switch ${showAddressInLink ? "active" : ""}`}
              >
                <span className="cset-switch-knob" />
              </span>
            </button>

            {showAddressInLink && (
              <div className="cset-sms-body">
                <p className="cset-hint">
                  אם לחדר שבו נקבעה הפגישה מוגדרת כתובת שונה, המטופל יראה את
                  כתובת החדר במקום כתובת הקליניקה.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderGooglePanel = () => (
    <div className="cset-card">
      <div className="cset-card-header">
        <span className="cset-card-icon google">
          <img src={gLogo} alt="Google" />
        </span>
        <div className="cset-card-heading">
          <h3>סנכרון עם Google Calendar</h3>
          <p>
            חבר את היומן שלך כדי שפגישות CliniKit יופיעו גם ב-Google Calendar.
            הפגישות מסונכרנות ליומן ייעודי בשם ״CLINIKIT״ כדי לשמור על סדר.
          </p>
        </div>
      </div>
      <div className="cset-google-wrap">
        <GoogleCalendarSync />
      </div>
    </div>
  );

  // Absences are self-contained (own RTK mutations, no save-all / dirty flow).
  const renderAbsencesPanel = () => (
    <AbsencesPanel
      targetTherapistId={targetTherapistId || undefined}
      targetTherapistName={targetTherapistName || undefined}
    />
  );

  const PANELS = {
    availability: renderAvailabilityPanel,
    absences: renderAbsencesPanel,
    booking: renderBookingPanel,
    sms: renderSmsPanel,
    google: renderGooglePanel,
  };

  return (
    <div className="modal-overlay cset-overlay" onClick={onClose}>
      <div className="cset-modal" onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <div className="cset-header">
          <h2>
            {availabilityOnly
              ? targetTherapistName
                ? `זמינות – ${targetTherapistName}`
                : "זמינות"
              : "הגדרות יומן"}
          </h2>
          <div className="cset-header-actions">
            <HelpButton featureKey="calendar_settings" />
            <ModalCloseButton onClick={onClose} />
          </div>
        </div>

        {/* MOBILE DOMAIN CHIPS */}
        {!availabilityOnly && (
          <div className="cset-chips">
            {DOMAINS.map((d) => (
              <button
                key={d.key}
                className={`cset-chip ${activeDomain === d.key ? "active" : ""}`}
                onClick={() => setActiveDomain(d.key)}
              >
                {d.short}
                {d.dirtyKey && dirty[d.dirtyKey] && (
                  <span className="cset-chip-dot" />
                )}
              </button>
            ))}
          </div>
        )}

        {/* BODY: rail + panel */}
        <div className="cset-body">
          {!availabilityOnly && (
            <nav className="cset-rail">
              {DOMAINS.map((d) => (
                <button
                  key={d.key}
                  className={`cset-rail-item ${activeDomain === d.key ? "active" : ""}`}
                  onClick={() => setActiveDomain(d.key)}
                >
                  <span className={`cset-rail-icon ${d.color}`}>
                    {d.key === "google" ? (
                      <img src={gLogo} alt="" />
                    ) : d.key === "availability" ? (
                      <CalendarBlank size={18} weight="fill" />
                    ) : d.key === "absences" ? (
                      <CalendarX size={18} weight="fill" />
                    ) : d.key === "booking" ? (
                      <LinkIcon size={18} weight="fill" />
                    ) : (
                      <ChatText size={18} weight="fill" />
                    )}
                  </span>
                  <span className="cset-rail-label">{d.label}</span>
                  {d.dirtyKey && dirty[d.dirtyKey] && (
                    <span className="cset-rail-dot" />
                  )}
                </button>
              ))}
            </nav>
          )}

          <div className="cset-panel">
            {availabilityOnly ? (
              <>
                {renderAvailabilityPanel()}
                {/* Admin editing a colleague: absences live next to availability */}
                {renderAbsencesPanel()}
              </>
            ) : (
              PANELS[activeDomain]()
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="cset-footer">
          <span className={`cset-footer-status ${anyDirty ? "dirty" : ""}`}>
            {anyDirty ? (
              <>
                <span className="cset-footer-dot" />
                יש שינויים שלא נשמרו
              </>
            ) : (
              "הכל שמור"
            )}
          </span>
          <button
            className="cset-btn-primary"
            onClick={handleSaveAll}
            disabled={!anyDirty || isSavingAll}
          >
            <FloppyDisk size={16} weight="bold" />
            {isSavingAll ? "שומר..." : "שמור הכל"}
          </button>
        </div>
      </div>

      {treatmentTypeModal && (
        <TreatmentTypeFormModal
          clinicId={clinicId}
          editingType={treatmentTypeModal.editing}
          onClose={() => setTreatmentTypeModal(null)}
        />
      )}
    </div>
  );
}

export default CalendarSettings;
